import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as MenuHamburgerIcon } from 'assets/icons/menu-hamburger.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/nav/notification.svg';
import { Section } from './navbar.interfaces';
import { Roles } from '../../firebase/interfaces';
import { useFetchUserByEmail } from '../../firebase/hooks';
import { useNavbarOptions } from 'hooks/useNavbarOptions';
import { ADMIN_ROLES } from 'utilities/constants';
import {
  COURSES,
  DASHBOARD,
  LOGOUT,
  MESSAGES,
  MY_APPOINTMENTS,
  MY_PATIENTS,
  NOTIFICATIONS,
  PROVIDER_SCHEDULED,
} from 'utilities/routes';
import { useGetFileUrlFromStorageMutation } from 'graphql/generated/hasura';
import { useAuth } from 'auth/context/AuthContext';
import Avatar from 'components/avatarComponent';
import Dropdown from 'components/dropdown/dropdownComponent';
import useDropdown from 'components/dropdown/useDropdown';
import { useNotificationCount } from 'notifications/contexts/NotificationsContext';
import { LINK_TO_CODEX_SHOP } from 'utilities/constants/navbar';
import { DropdownBodyContainer } from '../dropdown/DropdownBodyContainer';
import { NotificationNavbarDropdown } from '../../app/notifications/components/NotificationaNavbarDropdown/NotificationNavbarDropdown';
import { useIndicators } from '../../context/UserIndicatorContext';
import { ReactComponent as EmailGrayIcon } from 'assets/icons/nav/email.svg';
import { ReactComponent as Calendar } from 'assets/icons/calendar.svg';
import ContactSupport from '../footer/ContactSupport';
import { prefetchProviderRoute } from '../../router/provider/providerLoaders';

interface AuthenticatedNavbarProps {
  openNavbarMobile: () => void;
}

export const ProviderAuthenticatedNavbar: React.FC<
  AuthenticatedNavbarProps
> = ({ openNavbarMobile }) => {
  const {
    locale,
    loadingOptions,
    MyAccountOptions,
    MyAccountRoutes,
    ProviderMyAccountOptions,
    ProviderMyAccountRoutes,
  } = useNavbarOptions();

  const { profilePicture, setProfilePicture, user } = useAuth();

  const { loggedUserIndicator } = useIndicators();
  const {
    internalNotificationCount: notificationsCountWithExternalNotifications,
  } = useNotificationCount();

  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation({});
  const { fetchUserByEmailPromise } = useFetchUserByEmail();

  const userRole = user?.role;
  const [selectedSection, setSelectedSection] = useState<Section>();
  const navigate = useNavigate();
  const location = useLocation();
  const firstRendered = useRef(true);

  const getMyAccountOptionsBasedOnRole = () => {
    switch (userRole) {
      case Roles.PATIENT:
        return MyAccountOptions;
      case Roles.PROVIDER:
        return ProviderMyAccountOptions;
      default:
        return MyAccountOptions;
    }
  };

  const getMyAccountRoutesBasedOnRole = () => {
    switch (userRole) {
      case Roles.PATIENT:
        return MyAccountRoutes;
      case Roles.PROVIDER:
        return ProviderMyAccountRoutes;
      default:
        return MyAccountRoutes;
    }
  };

  useEffect(() => {
    const getProfilePicture = async (profilePicture: string) => {
      const image = await getFileUrlFromStorage({
        variables: {
          fileId: profilePicture,
        },
      });
      setProfilePicture(image.data?.GetSignUrlFormStorage?.url || null);
    };
    if (
      (userRole === Roles.PATIENT ||
        userRole === Roles.PROVIDER ||
        ADMIN_ROLES.includes(userRole as Roles)) &&
      user &&
      user.profilePicture &&
      firstRendered.current
    ) {
      getProfilePicture(user.profilePicture);
      firstRendered.current = false;
    }
  }, [
    userRole,
    user,
    getFileUrlFromStorage,
    setProfilePicture,
    fetchUserByEmailPromise,
  ]);

  useEffect(() => {
    const route = location.pathname.split('/')[1];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location]);

  const {
    openDropdownId,
    handleDropdownClick,
    registerDropdownRef,
    handleHoverOut,
  } = useDropdown();

  const {
    openDropdownId: isOpenNotificationsDropdown,
    handleDropdownClick: handleNotificationsDropdownClick,
    registerDropdownRef: registerNotificationsDropdownRef,
    handleHoverOut: handleNotificationsDropdownHoverOut,
  } = useDropdown();

  const handleDropdownOptionSelected = async (
    section: Section,
    subRoute: string,
  ) => {
    if (subRoute === MyAccountRoutes[locale?.myAccountOptions.logOut]) {
      navigate(LOGOUT);
    } else if (section === Section.Shop) {
      window.open(
        `${locale?.shopOptionsUrl}${subRoute}`,
        '_blank',
        'noreferrer',
      );
    } else if (
      section === Section.MyAccount ||
      section === Section.ManageTestKits ||
      section === Section.Providers ||
      section === Section.Appointments ||
      section === Section.Courses
    ) {
      navigate(subRoute);
    } else if (section === Section.MySkin) {
      navigate(`${subRoute}`);
    } else {
      navigate(`/${section}/${subRoute}`);
    }
  };

  const isSectionSelected = (section: Section): boolean =>
    selectedSection === section;

  if (loadingOptions) return null;

  return (
    <div className="flex flex-row h-full">
      <NavLink
        to={DASHBOARD}
        onMouseEnter={() => prefetchProviderRoute(DASHBOARD)}
        className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid={'link-dashboard'}
      >
        <div className="flex h-[84px] items-center pt-1.5">
          <div
            className={`text-base font-bold ${
              isSectionSelected(Section.Dashboard)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          >
            {locale?.dashboard}
          </div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected(Section.Dashboard) ? 'bg-clc-blue' : ''
          }`}
        />
      </NavLink>
      <NavLink
        to={MY_PATIENTS}
        onMouseEnter={() => prefetchProviderRoute(MY_PATIENTS)}
        className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid={'link-my-patients'}
      >
        <div className="flex h-[84px] items-center pt-1.5">
          <div
            className={`text-base font-bold ${
              isSectionSelected(Section.MyPatients)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          >
            {locale?.myPatients}
          </div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected(Section.MyPatients) ? 'bg-clc-blue' : ''
          }`}
        />
      </NavLink>
      <NavLink
        to={MY_APPOINTMENTS}
        onMouseEnter={() => prefetchProviderRoute(MY_APPOINTMENTS)}
        className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid={'link-my-appointments'}
      >
        <div className="flex h-[84px] items-center pt-1.5">
          <div
            className={`text-base font-bold ${
              isSectionSelected(Section.Appointments)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          >
            {locale?.appointments}
          </div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected(Section.Appointments) ? 'bg-clc-blue' : ''
          }`}
        />
      </NavLink>

      <NavLink
        to={COURSES}
        onMouseEnter={() => prefetchProviderRoute(COURSES)}
        className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid={'link-courses'}
      >
        <div className="flex h-[84px] items-center pt-1.5">
          <div
            className={`text-base font-bold uppercase ${
              isSectionSelected(Section.Courses)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          >
            {'Education'}
          </div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected(Section.Courses) ? 'bg-clc-blue' : ''
          }`}
        />
      </NavLink>

      <a
        href={LINK_TO_CODEX_SHOP}
        className="hidden cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid={'link-my-appointments'}
      >
        <div className="flex h-[84px] items-center pt-1.5 px-2.5">
          <div
            className={`text-base font-bold flex items-center ${
              isSectionSelected(Section.Shop)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          >
            <span className="mr-[5px] uppercase">
              {locale?.shopTests || 'Shop tests'}
            </span>
          </div>
        </div>
        <div className="absolute bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 ${
            isSectionSelected(Section.Shop) ? 'bg-clc-blue' : ''
          }`}
        />
      </a>

      <div className="hidden desktop:flex desktop:flex-row">
        <div className="my-auto">
          <div className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement">
            <div className="flex h-[84px] items-center">
              <ContactSupport />
            </div>
            <div className="absolute -left-0 -bottom-1 w-full h-[30px] navBarHoverDisplay" />
          </div>
        </div>

        <NavLink
          to={MESSAGES}
          onMouseEnter={() => prefetchProviderRoute(MESSAGES)}
          className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
          data-testid={'link-messages'}
        >
          <div className="flex h-[84px] items-center pt-1.5 relative">
            {loggedUserIndicator?.inMail && (
              <div className="absolute z-10 top-8 -right-0.5 border border-white h-2.5 w-2.5 rounded-full bg-orange-body-dot" />
            )}
            <EmailGrayIcon
              className={`${
                isSectionSelected(Section.Messages)
                  ? 'text-clc-blue'
                  : 'text-med-gray'
              }`}
            />
          </div>

          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected(Section.Messages) ? 'bg-clc-blue' : ''
            }`}
          />
        </NavLink>

        <NavLink
          to={PROVIDER_SCHEDULED}
          onMouseEnter={() => prefetchProviderRoute(PROVIDER_SCHEDULED)}
          className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
          data-testid={'link-calendar'}
        >
          <div className="flex h-[84px] items-center pt-1.5 relative">
            <Calendar
              className={`${
                isSectionSelected(Section.Calendar)
                  ? 'fill-clc-blue'
                  : 'fill-med-gray'
              }`}
            />
          </div>

          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected(Section.Calendar) ? 'bg-clc-blue' : ''
            }`}
          />
        </NavLink>

        <div
          className="cursor-pointer relative"
          onClick={() =>
            handleNotificationsDropdownClick(Section.Notifications, true)
          }
          onMouseEnter={() => prefetchProviderRoute(NOTIFICATIONS)}
          data-testid={'link-notifications'}
        >
          <div
            className="group"
            ref={(ref) =>
              registerNotificationsDropdownRef(Section.Notifications, ref)
            }
          >
            <div className="flex h-[84px] items-center pt-1.5 px-3 relative">
              {notificationsCountWithExternalNotifications > 0 && (
                <div className="absolute top-[30px] right-3 border border-white h-2.5 w-2.5 rounded-full bg-orange-body-dot" />
              )}
              <NotificationIcon
                className={`h-6 w-6 ${
                  isSectionSelected(Section.Notifications) ||
                  isOpenNotificationsDropdown === Section.Notifications
                    ? 'fill-clc-blue'
                    : 'fill-med-gray'
                }`}
              />
            </div>
            <div className="absolute left-0 bottom-0 w-full h-[30px] group-hover:bg-pnav-hover" />
            <div
              className={`flex h-1.5 ${
                isSectionSelected(Section.Notifications) ? 'bg-clc-blue' : ''
              }`}
            />

            {
              <DropdownBodyContainer align={'right'}>
                {isOpenNotificationsDropdown === Section.Notifications && (
                  <NotificationNavbarDropdown
                    isOpen
                    onClose={handleNotificationsDropdownHoverOut}
                  />
                )}
              </DropdownBodyContainer>
            }
          </div>
        </div>

        <div
          className="hidden desktop:flex flex-col cursor-pointer relative"
          onClick={() => handleDropdownClick(Section.MyAccount)}
          onMouseEnter={() => {
            handleDropdownClick(Section.MyAccount);
            prefetchProviderRoute(Section.MyAccount);
          }}
          onMouseLeave={() => handleHoverOut()}
        >
          <div
            className="group"
            ref={(ref) => registerDropdownRef(Section.MyAccount, ref)}
          >
            <div
              className="flex h-[84px] w-16 items-center cursor-pointer justify-center pt-1 px-0"
              data-testid={'link-my-profile'}
              onClick={() =>
                handleDropdownOptionSelected(
                  Section.MyAccount,
                  getMyAccountRoutesBasedOnRole()[
                    locale?.myAccountOptions.myProfile
                  ].toString(),
                )
              }
            >
              <Avatar
                size="sm"
                imageUrl={profilePicture}
                key={profilePicture}
              />
            </div>
            <div className="absolute bottom-0 w-full h-[30px] group-hover:bg-pnav-hover" />
            <div
              className={`flex h-1.5 ${
                isSectionSelected(Section.MyAccount) ? 'bg-clc-blue' : ''
              }`}
            />
            {openDropdownId === Section.MyAccount && (
              <Dropdown
                options={getMyAccountOptionsBasedOnRole()}
                right={true}
                zIndexClassName="z-[51]"
                onClick={(event, option) =>
                  handleDropdownOptionSelected(
                    Section.MyAccount,
                    getMyAccountRoutesBasedOnRole()[option].toString(),
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col desktop:hidden cursor-pointer relative">
        <NavLink to={NOTIFICATIONS} className="cursor-pointer relative">
          <div className="flex h-[70px] w-[45px] items-center justify-center">
            {notificationsCountWithExternalNotifications > 0 && (
              <div className="absolute top-[30px] right-2 border border-white h-2.5 w-2.5 rounded-full bg-orange-body-dot" />
            )}
            <NotificationIcon
              className={`${
                isSectionSelected(Section.Notifications) ||
                isOpenNotificationsDropdown === Section.Notifications
                  ? 'fill-clc-blue'
                  : 'fill-med-gray'
              }`}
            />
          </div>
          <div
            className={`absolute bottom-0 w-full h-1.5 ${
              isSectionSelected(Section.Notifications) ? 'bg-clc-blue' : ''
            }`}
          />
        </NavLink>
      </div>
      <div className="flex flex-col desktop:hidden">
        <div
          className="flex h-[70px] w-[45px] items-center justify-center cursor-pointer"
          onClick={openNavbarMobile}
        >
          <Avatar size="xs" imageUrl={profilePicture} key={profilePicture} />
        </div>
      </div>
      <div className="flex desktop:hidden">
        <div className="flex h-[70px] desktop:h-[84px] w-[45px] desktop:w-16 items-center justify-center desktop:pt-1 desktop:px-0">
          <MenuHamburgerIcon
            className="h-8 w-[45px] cursor-pointer"
            onClick={openNavbarMobile}
          />
        </div>
      </div>
    </div>
  );
};
